/**
 * StreamingModule — AOI-region asset streaming + prefetch + LRU eviction (Graphics G5).
 *
 * Rides the existing AOI spine (AtlasNetworkModule.maintainAoi @ FixedSim/80): each tick it
 * computes the player's 256 m region + movement vector, PREFETCHES the region(s) ahead from
 * the server-authoritative `regionAssetSet` manifest (HOLDING a reference so they stay
 * resident until passed), RELEASES regions that left the prefetch window (→ cache-warm,
 * LRU-evictable), and reclaims cache-warm assets over the memory budget. The asset is warm
 * before its structures enter the AOI subscription → StructureRenderModule's load is a cache
 * hit → no hitch. The budget = the quality tier × the world's Land-Impact cap.
 */
import type { App, IModule } from 'enginex';
import type { DbConnection } from '../module_bindings/index.js';
import { atlas } from '../state.js';
import { bus } from '../bus.js';
import { makeSystem, Phase } from '../engine-compat.js';
import type { AssetModule } from './AssetModule.js';
import { activePreset, setActiveTier, hasSavedSettings, type Tier } from '../lib/quality_tiers.js';
import { effectiveBudgetMb } from '../lib/memory_budget.js';

const REGION_M = 256;
const regionOf = (coord: number): number => Math.floor(coord / REGION_M);

export class StreamingModule implements IModule {
  readonly name = 'Streaming';
  private app!: App;
  private readonly pinned = new Map<string, Array<() => void>>(); // regionKey → release closures of HELD assets
  private subscribed = false;

  constructor(private readonly assets: AssetModule) {}

  init(app: App): void {
    this.app = app;
    app.world.addSystem(makeSystem('AtlasStreaming', Phase.FixedSim, 100, () => this.update())); // after AOI@80
    bus.on('atlas:account-ready', () => { this.subscribe(); this.applyDeviceTier(); });
    bus.on('atlas:world-entered', () => this.reset());
    bus.on('atlas:world-cleared', () => this.reset());
    // A tier change alters what load() bakes into a master (mip caps, LOD pick)
    // — flush the warm cache so nothing serves the OLD tier's representation.
    bus.on('atlas:quality-changed', () => { this.assets.evictToBudget(0); });
  }

  private subscribe(): void {
    if (this.subscribed) return;
    const conn = atlas.conn;
    if (!conn) return;
    this.subscribed = true;
    conn.subscriptionBuilder()
      .onApplied(() => this.applyDeviceTier())
      .subscribe(['SELECT * FROM region_asset_set', 'SELECT * FROM world_build_budget', 'SELECT * FROM device_tier_hint']);
  }

  /** Honor the account's saved tier preference (low|med|high; 'auto' keeps the default). */
  private applyDeviceTier(): void {
    const conn = atlas.conn;
    if (!conn || !atlas.identityHex) return;
    // FIRST-login seeding only: this browser's saved settings always win. The
    // unconditional apply stomped persisted/custom settings on EVERY login and
    // reverted any saved tier outside the hint's low|med|high vocabulary
    // (a saved 'potato' or 'ultra' silently became the hint each session).
    if (hasSavedSettings()) return;
    const hint = [...conn.db.deviceTierHint.iter()].find((h) => h.identity.toHexString() === atlas.identityHex);
    if (hint && (hint.tier === 'low' || hint.tier === 'med' || hint.tier === 'high')) {
      setActiveTier(hint.tier as Tier);
      bus.emit('atlas:quality-changed', {});
    }
  }

  /** Release every held region (world hop / clear) → their assets become cache-warm + evictable. */
  private reset(): void {
    for (const rels of this.pinned.values()) for (const rel of rels) rel();
    this.pinned.clear();
  }

  private tickAcc = 0;

  private update(): void {
    const conn = atlas.conn;
    if (!conn || atlas.currentWorldId === null) return;
    // ~4 Hz: the 256 m prefetch window and the cache-eviction pass change on
    // the scale of seconds — running them every 60 Hz physics tick allocated a
    // Set + key strings and walked the asset cache 60×/s (perf sweep 2026-08-22).
    if (++this.tickAcc < 15) return;
    this.tickAcc = 0;
    const window = this.windowKeys();
    // Unpin regions that left the prefetch window (→ cache-warm, LRU-evictable).
    for (const [key, rels] of [...this.pinned]) {
      if (!window.has(key)) { for (const rel of rels) rel(); this.pinned.delete(key); }
    }
    // Pin new regions: prefetch + HOLD their assets so they stay resident until the player passes.
    for (const key of window) if (!this.pinned.has(key)) { this.pinned.set(key, []); void this.pinRegion(conn, key); }
    // Reclaim cache-warm assets over the budget (LRU — referenced/visible assets are never evicted).
    this.assets.evictToBudget(this.budgetMb(conn));
  }

  /** The prefetch window: the current 256 m region + `prefetchRegions` regions ahead along the movement vector. */
  private windowKeys(): Set<string> {
    const rx = regionOf(atlas.localX), rz = regionOf(atlas.localZ);
    const keys = new Set<string>([`${rx},${rz}`]);
    const ahead = activePreset().prefetchRegions;
    const dirX = Math.abs(atlas.intentX) > 0.1 ? Math.sign(atlas.intentX) : 0;
    const dirZ = Math.abs(atlas.intentZ) > 0.1 ? Math.sign(atlas.intentZ) : 0;
    for (let i = 1; i <= ahead; i++) {
      if (dirX || dirZ) keys.add(`${rx + dirX * i},${rz + dirZ * i}`);
      if (dirX) keys.add(`${rx + dirX * i},${rz}`);
      if (dirZ) keys.add(`${rx},${rz + dirZ * i}`);
    }
    return keys;
  }

  private async pinRegion(conn: DbConnection, key: string): Promise<void> {
    const set = this.setFor(conn, key);
    if (!set) return; // no manifest for this region (unstreamed / empty)
    // Capture the array this pin owns: a world hop resets the map and a re-pin
    // of the same "rx,rz" key in the new world would otherwise receive the old
    // world's release closures.
    const owned = this.pinned.get(key);
    const ids = set.assetIds.split(',').filter(Boolean).map((s) => BigInt(s));
    for (const id of ids) {
      // Tier-matched prefetch: warm the representation the consumers will
      // actually acquire (potato → LOD1 variant). Pinning LOD0 canonicals on a
      // LOD1 tier double-paid: 21 MB fetches + 2048² uploads NOTHING rendered.
      let release: () => void;
      try { ({ release } = await this.assets.loadForTier(id)); }
      catch { continue; } // prefetch is best-effort
      const held = this.pinned.get(key);
      if (!held || held !== owned) { release(); continue; } // region left the window (or was re-pinned) mid-load → unhold
      held.push(release);
    }
  }

  private setFor(conn: DbConnection, key: string) {
    const [rx, rz] = key.split(',').map(Number);
    const w = atlas.currentWorldId;
    return [...conn.db.regionAssetSet.iter()].find((s) => s.worldId === w && s.regionX === rx && s.regionZ === rz);
  }

  private budgetMb(conn: DbConnection): number {
    const w = atlas.currentWorldId;
    const b = w !== null ? [...conn.db.worldBuildBudget.iter()].find((x) => x.worldId === w) : undefined;
    return effectiveBudgetMb(activePreset().memoryBudgetMb, b ? Number(b.liMax) : 0);
  }

  /** Test/inspection hooks. */
  get pinnedRegions(): string[] { return [...this.pinned.keys()].sort(); }
  budgetMbValue(): number { const conn = atlas.conn; return conn ? this.budgetMb(conn) : 0; }
  tick(): void { this.update(); }
}
